import { useTranslation } from '@starsoft/common/hooks';
import { PropsWithChildren } from 'react';
import { ErrorChip } from '../ErrorChip';
import { FormGroupProps } from './props';
import FormGroupSkeletonDescription from './Skeleton/Description';
import FormGroupSkeletonLabel from './Skeleton/Label';
import styles from './styles.module.scss';

/**
 * FormGroup Component
 * A form group component that wraps form elements with optional label, description and loading states
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.label] - Label text for the form group
 * @param {ReactNode} props.children - Child elements to render inside form group
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {string} [props.description] - Description text below label
 * @param {boolean} [props.loading] - Loading state for skeleton components
 * @param {boolean} [props.isLoading] - Whether to show loading skeleton state
 * @param {boolean} [props.optional] - Whether to show optional label text
 * @param {boolean} [props.alignCenter] - Whether to center align content
 * @param {ReactNode} [props.action] - Action element to show next to label
 * @param {Error} [props.error] - Error object to show error state
 * @param {Function} [props.lightStyle] - Callback function for error retry
 * @param {boolean} [props.noGap] - Whether to remove gap between label and description
 * @param {boolean} [props.bigLabel] - Whether to show big label
 * @returns {JSX.Element} Form group component with label, description and children
 */
export function FormGroup({
  label,
  children,
  className = '',
  description,
  loading,
  isLoading,
  optional,
  optionalLabel,
  alignCenter,
  action,
  error,
  refetch,
  secondary,
  hidden,
  bottomOptionalLabel,
  lightStyle,
  noGap,
  bigLabel,
}: PropsWithChildren<FormGroupProps>): JSX.Element {
  const { t } = useTranslation('common');

  if (error) {
    return <ErrorChip action={refetch} />;
  }

  if (hidden) {
    return null;
  }

  return (
    <div
      className={`${styles.container} ${alignCenter ? styles['container--align-center'] : ''} ${className} ${noGap ? styles['container--no-gap'] : ''}`}
    >
      {isLoading ? (
        <FormGroupSkeletonLabel loading={loading} />
      ) : (
        label && (
          <label
            className={`${styles.container__label} ${bigLabel ? styles['container__label--big'] : ''} ${lightStyle ? styles['container__label--light'] : ''} ${action ? styles['container__label--action'] : ''} ${secondary ? styles['container__label--secondary'] : ''} ${bottomOptionalLabel ? styles['container__label--bottom-optional-label'] : ''} ${alignCenter ? styles['container__label--text-align'] : ''}`}
          >
            {label} {action ? action : null}
            {optional && !optionalLabel && !bottomOptionalLabel && (
              <span
                className={`${styles.container__label} ${styles['container__label--secondary']}`}
              >
                ({t('optional_label')})
              </span>
            )}
            {optional && optionalLabel && (
              <span
                className={`${styles.container__label} ${styles['container__label--secondary']}`}
              >
                {optionalLabel}
              </span>
            )}
            {optional && bottomOptionalLabel && (
              <span
                className={`${styles.container__label} ${styles['container__label--thirdiary']}`}
              >
                {bottomOptionalLabel}
              </span>
            )}
          </label>
        )
      )}
      {isLoading ? (
        <FormGroupSkeletonDescription loading={loading} />
      ) : (
        description && (
          <label
            className={`${styles['container__description']} ${
              lightStyle ? styles['container__description--light'] : ''
            }`}
          >
            {description}
          </label>
        )
      )}
      {children}
    </div>
  );
}
